// src/pages/HomeSignedIn.jsx
// The signed-in landing page, served at /home. The marketing page at "/" stays
// as it is for visitors who aren't signed in; Navbar's Home button picks between
// the two on `user`.
//
// Same layout shell as Dashboard and Analytics: the existing Sidebar on the left,
// content in main. Only the content is new. Every colour comes from theme.js.
// main carries a photo under a dark scrim, so the two section headings sit on it
// in white; the pulse tiles and right-now cards keep their opaque surface.*
// grounds, which is what keeps their ink text at the contrast it had on the
// light page.
//
// This is a landing page, not Dashboard: no role panels, no assignment queues.
import React, { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { alpha, keyframes } from '@mui/material/styles';
import {
  CallSplit,
  EditNote,
  LocalFireDepartment,
  RateReview,
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import Sidebar from '../components/dashboard/Sidebar';
import ActivityMarquee from '../components/common/ActivityMarquee';
import { API_BASE } from '../utils/api';
import homepageImage from '../homepageImage.jpg';
import '../styles/Dashboard.css';
import '../styles/Sidebar.css';

// Root-relative, like /images/revisionari-logo.png: served from public/, not bundled.
const PAGE_BACKGROUND = '/images/home-bg.jpg';

const COMING_SOON = 'Coming soon';

const blink = keyframes`
  0%, 49% { opacity: 1; }
  50%, 100% { opacity: 0; }
`;

function authHeaders() {
  const token =
    localStorage.getItem('rv_access_token') ||
    localStorage.getItem('access_token') ||
    localStorage.getItem('rv_id_token') ||
    localStorage.getItem('id_token');
  return {
    Accept: 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

// "3 hours ago" / "yesterday" / a date once it's old enough to need one.
//
// Every unit floors rather than rounds, which is what relative time normally
// does: 90 minutes reads "1 hour ago", not "2 hours ago". Returns '' for a
// missing or unparseable value so the caller's " · edited …" suffix disappears
// instead of printing "edited Invalid Date".
//
// Exported for unit tests; not used outside this file.
export function lastEditedLabel(value) {
  if (!value) return '';
  const then = new Date(value);
  if (Number.isNaN(then.getTime())) return '';
  const mins = Math.floor((Date.now() - then.getTime()) / 60000);
  if (mins < 2) return 'just now';
  if (mins < 60) return `${mins} minutes ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days} days ago`;
  return then.toLocaleDateString();
}

// Turn "&amp;" into "&". The excerpt is rendered as a React string, not as
// HTML, so anything left encoded shows literally — a draft containing "Tom &
// Jerry" would read "Tom &amp; Jerry" in the hero. Decoding via the parser
// rather than a replace table covers every named and numeric entity.
function decodeEntities(value) {
  if (!value.includes('&')) return value;
  try {
    const doc = new DOMParser().parseFromString(value, 'text/html');
    return doc.documentElement.textContent || value;
  } catch {
    return value.replace(/&nbsp;/g, ' ');
  }
}

// Draft bodies come out of the editor as HTML. Strip the tags, collapse the
// whitespace, and keep enough for two lines. Empty after stripping returns '',
// which is what triggers the "This draft is still empty." fallback.
//
// Order matters: tags are stripped to a SPACE before decoding, so that
// "<p>a</p><p>b</p>" reads "a b" rather than "ab" — taking textContent off the
// parsed document instead would run every block element together.
//
// Known limit: a bare "<" in body text (as opposed to the "&lt;" the editor
// actually writes) is eaten along with everything up to the next ">", because
// the strip is a regex and not a parser.
//
// Exported for unit tests; not used outside this file.
export function excerptFrom(content, limit = 180) {
  if (!content || typeof content !== 'string') return '';
  const text = decodeEntities(content.replace(/<[^>]*>/g, ' '))
    .replace(/\s+/g, ' ')
    .trim();
  if (!text) return '';
  return text.length > limit ? `${text.slice(0, limit).trimEnd()}…` : text;
}

/** One tile in the writing-pulse row. `value === COMING_SOON` dims it. */
function PulseCard({ testId, label, icon, value, subtext }) {
  const soon = value === COMING_SOON;
  return (
    <Box
      data-testid={testId}
      sx={{
        bgcolor: 'surface.tile',
        border: '0.5px solid',
        borderColor: 'surface.border',
        borderRadius: 2.5,
        p: 2,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
        <Typography sx={{ fontSize: 12, color: 'surface.muted' }}>{label}</Typography>
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: (t) => alpha(t.palette.primary.main, 0.1),
            color: 'primary.main',
          }}
        >
          {icon}
        </Box>
      </Box>
      <Typography
        sx={{
          fontSize: soon ? 14 : 26,
          fontWeight: soon ? 500 : 800,
          color: soon ? 'surface.muted' : 'surface.ink',
          lineHeight: 1.2,
        }}
      >
        {value}
      </Typography>
      {subtext && (
        <Typography sx={{ fontSize: 12, color: 'surface.muted', mt: 0.75 }}>{subtext}</Typography>
      )}
    </Box>
  );
}

/** One card in the "Right now on ReVisionari" row. */
function DashCard({ testId, title, children }) {
  return (
    <Box
      data-testid={testId}
      sx={{
        bgcolor: 'surface.card',
        border: '0.5px solid',
        borderColor: 'surface.border',
        borderRadius: 2.5,
        p: 2.5,
      }}
    >
      <Typography sx={{ fontSize: 12, color: 'surface.muted', mb: 2 }}>{title}</Typography>
      {children}
    </Box>
  );
}

function EmptyNotice({ children }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 96 }}>
      <Typography sx={{ fontSize: 11, color: 'surface.muted', fontStyle: 'italic' }}>
        {children}
      </Typography>
    </Box>
  );
}

export default function HomeSignedIn() {
  const [sizes, setSizes] = useState({ top: 56, bottom: 56 });
  const [analytics, setAnalytics] = useState(null);
  const [latestDraft, setLatestDraft] = useState(null);
  const [loadingDraft, setLoadingDraft] = useState(true);

  useLayoutEffect(() => {
    const header =
      document.querySelector('.global-header') || document.querySelector('header');
    const footer =
      document.querySelector('.global-footer') || document.querySelector('footer');
    setSizes({
      top: header ? header.getBoundingClientRect().height : 56,
      bottom: footer ? footer.getBoundingClientRect().height : 56,
    });
  }, []);

  // The writing-pulse figures. tzOffset lets the backend end the streak's
  // "today" at the author's own midnight rather than UTC's.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const tzOffset = new Date().getTimezoneOffset();
        const res = await fetch(`${API_BASE}/api/author/analytics?tzOffset=${tzOffset}`, {
          credentials: 'omit',
          headers: authHeaders(),
        });
        if (!res.ok) throw new Error(`Request failed (${res.status})`);
        const data = await res.json();
        if (!cancelled) setAnalytics(data);
      } catch (e) {
        console.error('Failed to load author analytics:', e);
        if (!cancelled) setAnalytics({});
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Two calls on purpose: /api/drafts is sorted lastEdited-desc but its projection
  // excludes `content`, so the excerpt has to come from the single-draft endpoint.
  const loadLatestDraft = useCallback(async () => {
    try {
      const listRes = await fetch(`${API_BASE}/api/drafts?type=manuscript`, {
        credentials: 'omit',
        headers: authHeaders(),
      });
      if (!listRes.ok) throw new Error(`Request failed (${listRes.status})`);
      const list = await listRes.json();
      const newest = Array.isArray(list) ? list[0] : null;
      if (!newest) return null;

      const id = newest._id || newest.id;
      const detailRes = await fetch(`${API_BASE}/api/drafts/${id}`, {
        credentials: 'omit',
        headers: authHeaders(),
      });
      const detail = detailRes.ok ? await detailRes.json() : {};
      return {
        id,
        title: newest.title || detail.title || 'Untitled draft',
        lastEdited: lastEditedLabel(newest.lastEdited || detail.lastEdited),
        excerpt: excerptFrom(detail.content),
      };
    } catch (e) {
      console.error('Failed to load latest draft:', e);
      return null;
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const draft = await loadLatestDraft();
      if (!cancelled) {
        setLatestDraft(draft);
        setLoadingDraft(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loadLatestDraft]);

  const cssVars = {
    '--rv-topbar-height': `${sizes.top}px`,
    '--rv-footer-height': `${sizes.bottom}px`,
    '--rv-sidebar-width': '240px',
  };

  const ideaBranches = analytics?.ideaBranches;
  const streak = analytics?.writingStreak;
  const feedbackRate = analytics?.peerFeedbackRate;
  const feedbackDecisions = analytics?.peerFeedbackDecisions;

  return (
    <div className="dashboard-layout" style={cssVars}>
      <Sidebar />
      <Box
        component="main"
        className="dashboard-main"
        data-testid="home-signed-in"
        sx={(t) => ({
          // The document scrolls, not main, so the photo scrolls with the page
          // and `cover` stretches it over main's full height (about 2.3x on a
          // phone, where it reads as dark texture). The scrim is what keeps the
          // white headings readable over the bright window and pages in the photo.
          backgroundImage: `linear-gradient(${alpha(t.palette.common.black, 0.55)}, ${alpha(
            t.palette.common.black,
            0.55
          )}), url(${PAGE_BACKGROUND})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        })}
      >
        {/* Hero — carries its own photo over the page's, under a heavier scrim.
            The gradient is derived from the theme rather than a fixed hex. */}
        <Box
          data-testid="home-hero"
          sx={(t) => ({
            position: 'relative',
            borderRadius: 2.5,
            overflow: 'hidden',
            p: { xs: 3, md: 5.5 },
            mb: 4.5,
            display: 'grid',
            // auto-fit, not a breakpoint: MUI's md/lg measure the VIEWPORT, but
            // this content sits inside a 240px sidebar plus 64px of padding, so
            // a viewport breakpoint fires ~304px too early and the chapter card
            // collapses to an unreadable column. Same approach as .tile-grid in
            // Dashboard.css. 360px is the narrowest the chapter card stays
            // readable at, so two columns need ~750px of actual content width.
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(360px, 100%), 1fr))',
            gap: 4,
            alignItems: 'center',
            backgroundImage: `linear-gradient(to right, ${alpha(
              t.palette.common.black,
              0.88
            )} 35%, ${alpha(t.palette.common.black, 0.3)}), url(${homepageImage})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center 25%',
            border: '1px solid',
            borderColor: alpha(t.palette.common.white, 0.18),
          })}
        >
          <Box>
            <Typography
              component="h1"
              sx={{
                fontFamily: 'Cambria, Georgia, serif',
                fontSize: { xs: 32, md: 44 },
                fontWeight: 700,
                lineHeight: 1.15,
                color: 'common.white',
                mb: 2,
              }}
            >
              Write. Share.
              <br />
              <Box component="span" sx={{ color: 'secondary.main' }}>
                Evolve.
              </Box>
            </Typography>
            <Typography
              sx={{
                fontSize: 15,
                color: (t) => alpha(t.palette.common.white, 0.9),
                lineHeight: 1.6,
                maxWidth: 440,
              }}
            >
              A collaborative home for every idea that wants to become a book.
            </Typography>
          </Box>

          {/* Live chapter — the author's most recently edited manuscript. */}
          <Box
            data-testid="home-live-chapter"
            sx={(t) => ({
              bgcolor: alpha(t.palette.primary.dark, 0.6),
              backdropFilter: 'blur(16px)',
              WebkitBackdropFilter: 'blur(16px)',
              border: '1px solid',
              borderColor: alpha(t.palette.common.white, 0.2),
              borderRadius: 2,
              p: 3,
            })}
          >
            {loadingDraft ? (
              <CircularProgress size={20} sx={{ color: 'common.white' }} />
            ) : latestDraft ? (
              <>
                <Typography
                  sx={{ fontSize: 13, color: (t) => alpha(t.palette.common.white, 0.65), mb: 1.5 }}
                >
                  {latestDraft.title}
                  {latestDraft.lastEdited ? ` · edited ${latestDraft.lastEdited}` : ''}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: 'Cambria, Georgia, serif',
                    fontSize: 15,
                    lineHeight: 1.8,
                    color: 'common.white',
                    mb: 2.5,
                  }}
                >
                  {latestDraft.excerpt || 'This draft is still empty.'}
                  <Box component="span" sx={{ animation: `${blink} 1s step-end infinite` }}>
                    |
                  </Box>
                </Typography>
                <Button
                  component={Link}
                  to={`/editor/${latestDraft.id}`}
                  size="small"
                  sx={{ color: 'secondary.main', px: 0 }}
                >
                  Continue writing
                </Button>
              </>
            ) : (
              <Typography
                sx={{
                  fontSize: 13,
                  color: (t) => alpha(t.palette.common.white, 0.65),
                  lineHeight: 1.8,
                }}
              >
                No manuscripts yet — Create, in the top bar, opens a blank page.
              </Typography>
            )}
          </Box>
        </Box>

        {/* Writing pulse. The word goal has no data source yet: there is no
            per-day word count and no stored goal, so it shows "Coming soon"
            rather than a number nobody can stand behind. The other three come
            from /api/author/analytics; a figure the request failed to bring
            back falls to "Coming soon" too. */}
        <Typography sx={{ fontSize: 18, fontWeight: 700, color: 'common.white', mb: 2.5 }}>
          Your writing pulse
        </Typography>
        <Box
          sx={{
            display: 'grid',
            // Container-relative for the same reason as the hero above.
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(220px, 100%), 1fr))',
            gap: 2.5,
            mb: 4.5,
          }}
        >
          <PulseCard
            testId="pulse-word-goal"
            label="Daily word goal"
            icon={<EditNote sx={{ fontSize: 18 }} />}
            value={COMING_SOON}
            subtext="Word counts aren't recorded yet"
          />
          <PulseCard
            testId="pulse-streak"
            label="Writing streak"
            icon={<LocalFireDepartment sx={{ fontSize: 18 }} />}
            value={
              typeof streak === 'number'
                ? `${streak.toLocaleString()} day${streak === 1 ? '' : 's'}`
                : COMING_SOON
            }
            subtext={
              typeof streak !== 'number'
                ? undefined
                : streak > 0
                  ? 'Days in a row you have written'
                  : 'Write today to start one'
            }
          />
          <PulseCard
            testId="pulse-idea-branches"
            label="Active idea branches"
            icon={<CallSplit sx={{ fontSize: 18 }} />}
            value={
              ideaBranches === null || ideaBranches === undefined
                ? COMING_SOON
                : ideaBranches.toLocaleString()
            }
            subtext="Comments reviewers left on your manuscripts"
          />
          <PulseCard
            testId="pulse-feedback-rate"
            label="Peer feedback rate"
            icon={<RateReview sx={{ fontSize: 18 }} />}
            value={
              typeof feedbackRate === 'number'
                ? `${Math.round(feedbackRate * 100)}%`
                : feedbackDecisions === 0
                  ? '—'
                  : COMING_SOON
            }
            subtext={
              typeof feedbackRate === 'number' && typeof feedbackDecisions === 'number'
                ? `Approved, of ${feedbackDecisions.toLocaleString()} review decision${
                    feedbackDecisions === 1 ? '' : 's'
                  } on your books`
                : feedbackDecisions === 0
                  ? 'No review decisions on your books yet'
                  : undefined
            }
          />
        </Box>

        {/* Right now on ReVisionari — ported from the marketing Home, minus the
            /author/:id links, which point at a route that isn't registered. */}
        <Typography sx={{ fontSize: 18, fontWeight: 700, color: 'common.white', mb: 2.5 }}>
          Right now on ReVisionari
        </Typography>
        <Box
          sx={{
            display: 'grid',
            // Container-relative for the same reason as the hero above.
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(260px, 100%), 1fr))',
            gap: 2.5,
            mb: 4,
          }}
        >
          <DashCard testId="right-now-authors" title="Top authors">
            <EmptyNotice>Coming soon — no author stats tracked yet</EmptyNotice>
          </DashCard>

          <DashCard testId="right-now-books" title="Top books">
            <EmptyNotice>Coming soon — no book/branch stats tracked yet</EmptyNotice>
          </DashCard>

          <DashCard testId="right-now-collaborators" title="Top collaborators">
            <EmptyNotice>Coming soon — no acceptance-rate stats tracked yet</EmptyNotice>
          </DashCard>
        </Box>

        <ActivityMarquee />
      </Box>
    </div>
  );
}
